import * as _ from 'lodash';
import * as moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { Dispatch, connect } from 'react-redux';
import { IReportState, IApplicationState, AppActionCreators, ReportActionCreators, IPreSelectHistoryDetailsResponse } from '@nkr/store/index';
import { GridWrapper } from './styles';
import { Alert, Button, FormControl, Well } from 'react-bootstrap';
import { ColDef, CsvExportParams, FirstDataRenderedEvent, GridApi, GridReadyEvent, ICellRendererParams, IHeaderParams, ProcessCellForExportParams } from 'ag-grid-community';
import { IPreSelectHistoryDetails } from '@nkr/models/preselect-history-details';
import { AgGridReact } from 'ag-grid-react';
import { ThirtyDayPreselectOverlay } from '@nkr/views/center-admin/action-center/center-performance/relative-performance-overlays';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import AuditReportScoreTable from '../../audit-report-score-table';

interface IOwnProps {
    centerId: number
}

interface IStateProps {
    reports: IReportState
}

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getPreselectHistoryDetailsReport: (centerId: number, startDate: string, endDate: string) => Promise<IPreSelectHistoryDetailsResponse>
}

type TProps = IStateProps & IDispatchProps & IOwnProps

const PreselectHistoryDetail = (props: TProps) => {
    const [data, setData] = useState<IPreSelectHistoryDetails[]>([]);
    const [average, setAverage] = useState(0);
    const [startDate, setStartDate] = useState(moment().add(-30, 'days').format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));
    const [showDateWarning, setShowDateWarning] = useState(false);
    const [gridApi, setGridApi] = useState<GridApi>(null);

    useEffect(() => {
        getData();
    }, []);

    const onGridReady = (e: GridReadyEvent) => {
        e.api.sizeColumnsToFit();

        setGridApi(e.api);
    };

    const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const getData = () => {
        // let sd = moment(startDate).format('YYYY-MM-DD');
        // let ed = moment(endDate).format('YYYY-MM-DD');

        props.showLoading('Getting report data, one moment please...').then(() => {
            props.getPreselectHistoryDetailsReport(props.centerId, startDate, endDate)
                .then(response => {
                    let { list, average } = response.data.results;

                    setData(list);
                    setAverage(average);
                })
                .catch((ex) => {
                    alert('An error occured while loading report data');
                })
                .finally(() => {
                    props.hideLoading();
                });
        });
    };

    const validate = () => {
        if (startDate == '' || endDate == '') {
            return false;
        }

        const yearRegex = /^[1-9]\d{3}-\d{2}-\d{2}$/;

        if (!yearRegex.test(startDate) || !yearRegex.test(endDate)) {
            return false;
        }

        let startDateMoment = moment(startDate, 'YYYY-MM-DD', true);
        let endDateMoment = moment(endDate, 'YYYY-MM-DD', true);

        if (startDateMoment.isValid() == false || endDateMoment.isValid() == false) {
            return false;
        }

        return true;
    }

    const handleDateChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        let { name, value } = e.currentTarget;

        if (name == 'startDate') {
            setStartDate(value);
        }

        if (name == 'endDate') {
            setEndDate(value);
        }
    };

    const handleRefresh = () => {
        let isValid = validate();

        setShowDateWarning(isValid == false);

        if (isValid) {
            getData();
        }
    };

    const handleExport = () => {
        let params: CsvExportParams = {
            columnGroups: true,
            fileName: 'preselect-history.csv',
            processCellCallback: (params: ProcessCellForExportParams) => {
                let colDef = params.column.getColDef();

                if (colDef.headerName == 'Rank')
                    return params.node.rowIndex + 1;
                else if (colDef.colId == 'xpDate')
                    return moment(params.value).format('MM/DD/YYYY');

                return params.value;
            }
        };

        gridApi.exportDataAsCsv(params);
    };

    const columns: ColDef[] = [
        {
            headerName: 'Date',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Date</SortableHeaderComponent>,
            field: 'preSelectAverageDate',
            headerClass: 'center-list-header',
            cellClass: 'center-list-cell',
            width: 400,
            cellRendererFramework: (params: ICellRendererParams) => {
                return <span>{moment(params.data.preSelectAverageDate).format('MM-DD-YYYY')}</span>
            }
        },

        {
            headerName: 'Percentage',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Percentage</SortableHeaderComponent>,
            headerClass: 'center-list-header',
            cellClass: 'center-list-cell',
            field: 'percentPreSelected',
            width: 450,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IPreSelectHistoryDetails;

                if (data.percentPreSelected < 0) {
                    return 'N/A';
                }

                return <>{data.percentPreSelected}</>
            }
        }
    ];

    const sortedData = _.sortBy(data, x => x.preSelectAverageDate);

    const columnDefs = useMemo(() => columns, []);
    const rowData = useMemo(() => sortedData, [data]);

    const startDateDisplay = moment(startDate).format('MM/DD/YYYY');
    const endDateDisplay = moment(endDate).format('MM/DD/YYYY');

    return (
        <>
            {showDateWarning && (
                <div style={{ backgroundColor: '#843534', color: 'white', padding: '2px 5px', borderTopLeftRadius: 4, borderTopRightRadius: 4, width: '50%' }}>
                    Please make sure that the start and end dates are valid...
                </div>
            )}

            {/* <AuditReportScoreTable
                reportName='30 Day Preselect %'
                scoreValue={`${average.toString()}%`}
                overlay={<ThirtyDayPreselectOverlay />}
            /> */}

            <Well bsSize='sm' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5, width: '60%' }}>
                <div style={{ display: 'flex', alignItems: 'center', marginRight: 20, fontSize: 12 }}>
                    <div style={{ marginRight: 5 }}>Start Date:</div>
                    <div className={showDateWarning ? 'has-error' : null} style={{ marginRight: 10 }}>
                        <FormControl
                            type='date'
                            name='startDate'
                            value={startDate}
                            onChange={handleDateChange}
                        />
                    </div>

                    <div style={{ marginRight: 5 }}>End Date:</div>
                    <div className={showDateWarning ? 'has-error' : null} style={{ marginRight: 10 }}>
                        <FormControl
                            type='date'
                            name='endDate'
                            value={endDate}
                            onChange={handleDateChange}
                        />
                    </div>

                    <div style={{ marginRight: 20 }}>
                        <Button bsStyle='primary' bsSize='sm' onClick={handleRefresh}>Refresh Report</Button>
                    </div>
                </div>
                <div>
                    <AuditReportScoreTable
                        reportName='30 Day Preselect %'
                        scoreValue={`${average.toString()}%`}
                        overlay={<ThirtyDayPreselectOverlay />}
                    />
                </div>
                <div>
                    <Button bsSize='sm' onClick={handleExport}>Export CSV</Button>
                </div>
            </Well>

            <Alert bsStyle='info' bsSize='sm' style={{ width: '60%', marginBottom: 5 }}>The average preselect rate for {startDateDisplay} - {endDateDisplay} was {average}%.</Alert>

            <GridWrapper className='ag-theme-bootstrap' style={{ height: '65vh', width: '60%' }}>
                <AgGridReact
                    columnDefs={columnDefs}
                    rowData={rowData}
                    suppressCellSelection={true}
                    rowSelection='single'
                    onGridReady={onGridReady}
                    onFirstDataRendered={onFirstDataRendered}
                    noRowsOverlayComponentFramework={() =>
                        <>
                            {data.length == 0 && (
                                <>No records found...</>
                            )}
                        </>
                    }
                    getRowStyle={(props: any) => {
                        let data = props.data as IPreSelectHistoryDetails;

                        if (data.isLowestScore) {
                            return { backgroundColor: 'yellow', fontWeight: 'bold' };
                        }
                    }}
                />
            </GridWrapper>
        </>
    );
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
        reports: appState.reports
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getPreselectHistoryDetailsReport: (centerId, startDate, endDate) => {
            return dispatch(ReportActionCreators.getPreselectHistoryDetailsReport(centerId, startDate, endDate));
        }
    }
}

export default connect<IStateProps, IDispatchProps, IOwnProps>(
    mapStateToProps,
    mapDispatchToProps)(PreselectHistoryDetail);